import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DropzoneArea } from 'react-mui-dropzone';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import SelectField from '../common/components/SelectField';
import deviceCategories from '../common/util/deviceCategories';
import LinkField from '../common/components/LinkField';
import { useTranslation } from '../common/components/LocalizationProvider';
import useDeviceAttributes from '../common/attributes/useDeviceAttributes';
import { useAdministrator } from '../common/util/permissions';
import SettingsMenu from './components/SettingsMenu';
import useCommonDeviceAttributes from '../common/attributes/useCommonDeviceAttributes';
import useFeatures from '../common/util/useFeatures';
import { useCatch } from '../reactHelper';
import { formatNotificationTitle } from '../common/util/formatter';
import { useAttributePreference } from '../common/util/preferences';
import { volumeFromLiters, volumeToLiters, volumeUnitString } from '../common/util/converter';

const useStyles = makeStyles((theme) => ({
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
}));

const DevicePage = () => {
  const classes = useStyles();
  const t = useTranslation();

  const admin = useAdministrator();

  const commonDeviceAttributes = useCommonDeviceAttributes(t);
  const deviceAttributes = useDeviceAttributes(t);

  const features = useFeatures();
  const volumeUnit = useAttributePreference('volumeUnit');

  const [item, setItem] = useState();

  const handleFiles = useCatch(async (files) => {
    if (files.length > 0) {
      const response = await fetch(`/api/devices/${item.id}/image`, {
        method: 'POST',
        body: files[0],
      });
      if (response.ok) {
        setItem({ ...item, attributes: { ...item.attributes, deviceImage: await response.text() } });
      } else {
        throw Error(await response.text());
      }
    }
  });

  const validate = () => item && item.name && item.uniqueId;

  const [fuelConsumptionInput, setFuelConsumptionInput] = useState(() => (item?.attributes?.targetFuelConsumption != null
    ? volumeFromLiters(item.attributes.targetFuelConsumption, volumeUnit).toString()
    : ''));

  const handleFuelChange = (event) => {
    const input = event.target.value;
    setFuelConsumptionInput(input);

    const parsed = parseFloat(input);
    if (!Number.isNaN(parsed)) {
      setItem({
        ...item,
        attributes: {
          ...item.attributes,
          targetFuelConsumption: volumeToLiters(parsed, volumeUnit),
        },
      });
    }
  };
  useEffect(() => {
    setFuelConsumptionInput(item?.attributes?.targetFuelConsumption != null
      ? volumeFromLiters(item.attributes.targetFuelConsumption, volumeUnit).toString()
      : '');
  }, [item, volumeUnit]);

  return (
    <EditItemView
      endpoint="devices"
      item={item}
      setItem={setItem}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['sharedDevice']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRequired')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.name || ''}
                onChange={(event) => setItem({ ...item, name: event.target.value })}
                label={t('sharedName')}
                disabled={!admin || features.disableDeviceEditInfo}
              />
              <TextField
                value={item.uniqueId || ''}
                onChange={(event) => setItem({ ...item, uniqueId: event.target.value })}
                label={t('deviceIdentifier')}
                helperText={t('deviceIdentifierHelp')}
                disabled={!admin || features.disableDeviceEditInfo}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedExtra')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <SelectField
                value={item.groupId || 0}
                onChange={(event) => setItem({ ...item, groupId: Number(event.target.value) })}
                endpoint="/api/groups"
                label={t('groupParent')}
                filterKey="attributes.type"
                filterValue="construction"
                disabled={features.disableDeviceEditInfo}
              />
              <TextField
                value={item.attributes?.vin || ''}
                onChange={(event) => setItem({ ...item, attributes: { ...item.attributes, vin: event.target.value } })}
                label={t('attributeVIN')}
                disabled={features.disableDeviceEditInfo}
              />
              <TextField
                value={item.attributes?.chassisNumber || ''}
                onChange={(event) => setItem({ ...item, attributes: { ...item.attributes, chassisNumber: event.target.value } })}
                label={t('attributeChassisNumber')}
                disabled={features.disableDeviceEditInfo}
              />
              <TextField
                value={item.attributes?.transitCode || ''}
                onChange={(event) => setItem({ ...item, attributes: { ...item.attributes, transitCode: event.target.value } })}
                label={t('attributeTransitCode')}
                disabled={features.disableDeviceEditInfo}
              />
              <TextField
                value={item.phone || ''}
                onChange={(event) => setItem({ ...item, phone: event.target.value })}
                label={t('sharedPhone')}
                disabled={features.disableDeviceEditInfo}
              />
              <TextField
                value={item.model || ''}
                onChange={(event) => setItem({ ...item, model: event.target.value })}
                label={t('deviceModel')}
                disabled={features.disableDeviceEditInfo}
              />
              <TextField
                value={item.contact || ''}
                onChange={(event) => setItem({ ...item, contact: event.target.value })}
                label={t('deviceContact')}
                disabled={features.disableDeviceEditInfo}
              />
              <SelectField
                value={item.category || 'default'}
                emptyValue={null}
                onChange={(event) => setItem({ ...item, category: event.target.value })}
                data={deviceCategories.map((category) => ({
                  id: category.label,
                  name: t(`category${category.label.replace(/^\w/, (c) => c.toUpperCase())}`),
                }))}
                label={t('deviceCategory')}
                disabled={features.disableDeviceEditInfo}
              />
              <TextField
                label={t('userExpirationTime')}
                type="date"
                value={(item.expirationTime && moment(item.expirationTime).locale('en').format(moment.HTML5_FMT.DATE)) || '2099-01-01'}
                onChange={(e) => setItem({ ...item, expirationTime: moment(e.target.value, moment.HTML5_FMT.DATE).locale('en').format() })}
                disabled={!admin || features.disableDeviceEditInfo}
              />
              <TextField
                value={fuelConsumptionInput}
                onChange={handleFuelChange}
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment sx={{ ml: 2 }} position="end">
                      {`${volumeUnitString(volumeUnit, t)}/${t('sharedHourAbbreviation')}`}
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.01 },
                }}
                label={t('positionFuelConsumption')}
                disabled={features.disableDeviceEditInfo}
              />
              <FormControlLabel
                control={<Checkbox checked={item.disabled} onChange={(event) => setItem({ ...item, disabled: event.target.checked })} />}
                label={t('sharedDisabled')}
              />
            </AccordionDetails>
          </Accordion>
          {(item.id && admin) && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {t('attributeDeviceImage')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <DropzoneArea
                  dropzoneText={t('sharedDropzoneText')}
                  acceptedFiles={['image/*']}
                  filesLimit={1}
                  onChange={handleFiles}
                  showAlerts={false}
                />
              </AccordionDetails>
            </Accordion>
          )}
          {admin && (
            <>
              <EditAttributesAccordion
                attributes={item.attributes}
                setAttributes={(attributes) => setItem({ ...item, attributes })}
                definitions={{ ...commonDeviceAttributes, ...deviceAttributes }}
                hiddenAttributes={['deviceImage', 'maintenance', 'operativeDate', 'maintenanceDate', 'maintenanceDetails']}
              />
              {item.id && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">
                      {t('sharedConnections')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.details}>
                    <LinkField
                      endpointAll="/api/geofences"
                      endpointLinked={`/api/geofences?deviceId=${item.id}`}
                      baseId={item.id}
                      keyBase="deviceId"
                      keyLink="geofenceId"
                      label={t('sharedGeofences')}
                    />
                    <LinkField
                      endpointAll="/api/notifications"
                      endpointLinked={`/api/notifications?deviceId=${item.id}`}
                      baseId={item.id}
                      keyBase="deviceId"
                      keyLink="notificationId"
                      titleGetter={(it) => formatNotificationTitle(t, it)}
                      label={t('sharedNotifications')}
                    />
                    {!features.disableDrivers && (
                      <LinkField
                        endpointAll="/api/drivers"
                        endpointLinked={`/api/drivers?deviceId=${item.id}`}
                        baseId={item.id}
                        keyBase="deviceId"
                        keyLink="driverId"
                        label={t('sharedDrivers')}
                      />
                    )}
                    {!features.disableComputedAttributes && (
                      <LinkField
                        endpointAll="/api/attributes/computed"
                        endpointLinked={`/api/attributes/computed?deviceId=${item.id}`}
                        baseId={item.id}
                        keyBase="deviceId"
                        keyLink="attributeId"
                        titleGetter={(it) => it.description}
                        label={t('sharedComputedAttributes')}
                      />
                    )}
                    <LinkField
                      endpointAll="/api/commands"
                      endpointLinked={`/api/commands?deviceId=${item.id}`}
                      baseId={item.id}
                      keyBase="deviceId"
                      keyLink="commandId"
                      titleGetter={(it) => it.description}
                      label={t('sharedSavedCommands')}
                    />
                    {!features.disableMaintenance && (
                      <LinkField
                        endpointAll="/api/maintenance"
                        endpointLinked={`/api/maintenance?deviceId=${item.id}`}
                        baseId={item.id}
                        keyBase="deviceId"
                        keyLink="maintenanceId"
                        label={t('sharedMaintenance')}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}
        </>

      )}
    </EditItemView>
  );
};

export default DevicePage;
